import { useMutation, useQuery } from '@tanstack/react-query'
import { apiClient, spreadsheetApiClient } from 'api/apiClient/apiClient'
import { saveAs } from 'file-saver'
import { getErrorStatus, queryClient, shouldRetryQuery } from 'helpers/queryClient'
import type { Channel } from 'pusher-js'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import alertStore from 'store/alert/alert'
import settlementExportStore from 'store/settlementExport/settlementExport'
import { useAuth } from 'ui/components/AuthContext/AuthContext'
import { useEvent, usePusherConnectionState } from 'ui/components/PusherContext/PusherContext'
import getLogger from 'utils/log/log'
import { useSnapshot } from 'valtio'
import { z } from 'zod'
import {
  SETTLEMENT_EXPORT_UPDATED_EVENT,
  SETTLEMENT_EXPORTS_CREATE_ENDPOINT,
  SETTLEMENT_EXPORTS_ENDPOINT,
  SETTLEMENT_EXPORTS_QUERY_KEY,
  settlementExportDownloadEndpoint,
} from './settlementExports.constants'
import {
  type CreateSettlementExportBody,
  type SettlementExportListWindow,
  type SettlementExportRecord,
  SettlementExportRecordSchema,
  type SettlementExportStatus,
  type SettlementExportUpdatedEvent,
  SettlementExportUpdatedEventSchema,
} from './settlementExports.types'

const log = getLogger('settlementExports.api')

const XLSX_CONTENT_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'

function isTerminalSettlementExportStatus(status: SettlementExportStatus): boolean {
  return status === 'Finished' || status === 'Failed'
}

/** A Finished event arrives before the refetch that fills in `file_name`, so both are required. */
export function isSettlementExportDownloadable(record: SettlementExportRecord): boolean {
  return record.status === 'Finished' && !!record.file_name
}

/** Processing can repeat (each retry sends it again); terminal statuses share the last step. */
const STATUS_PROGRESS: Record<SettlementExportStatus, number> = {
  Created: 0,
  Processing: 1,
  Finished: 2,
  Failed: 2,
}

function canPatchStatus(record: SettlementExportRecord, event: SettlementExportUpdatedEvent): boolean {
  return (
    record.id === event.id &&
    !isTerminalSettlementExportStatus(record.status) &&
    STATUS_PROGRESS[event.status] >= STATUS_PROGRESS[record.status]
  )
}

/**
 * The backend answers in three shapes: `{ field: [...] }`, a bare `[...]` (wrong system, no
 * masterdata sheet), and `{ detail: "..." }`. Its messages are shown as they are — DRF's generic
 * ones are localized, so they are never matched on.
 */
export function getSettlementExportErrorMessage(error: unknown): string | undefined {
  const body = error instanceof Error && 'body' in error ? error.body : undefined
  const messages = Array.isArray(body)
    ? body
    : body !== null && typeof body === 'object'
      ? Object.values(body).flatMap((value) => (Array.isArray(value) ? value : [value]))
      : []
  const message = messages.find((value) => typeof value === 'string' && value.trim())
  return typeof message === 'string' ? message : undefined
}

/**
 * Moves a cached row forward to the event's status, for instant feedback. A row never moves back and
 * never leaves a terminal status here: events can arrive late or out of order.
 */
export function applySettlementExportEvent(
  records: SettlementExportRecord[],
  event: SettlementExportUpdatedEvent
): SettlementExportRecord[] {
  return records.map((record) => (canPatchStatus(record, event) ? { ...record, status: event.status } : record))
}

/**
 * The event carries only an id and a status, so it can patch a row the cache already holds but not
 * create one. A progress status on a cached export is patched; anything else is refetched — an
 * export no cached list holds yet (another user's, or an operator's re-run), and a terminal status,
 * whose refetch brings the file name and is the only thing allowed to move a row out of a terminal
 * status.
 */
export function handleSettlementExportUpdatedEvent(systemId: number, data: unknown): void {
  const parsed = SettlementExportUpdatedEventSchema.safeParse(data)
  if (!parsed.success) {
    log.error('Received invalid settlement_export_updated payload:', parsed.error)
    return
  }
  const event = parsed.data
  const queryKey = [SETTLEMENT_EXPORTS_QUERY_KEY, { system: systemId }]

  const isCachedExport = queryClient
    .getQueriesData<SettlementExportRecord[]>({ queryKey })
    .some(([, records]) => records?.some((record) => record.id === event.id))

  if (isTerminalSettlementExportStatus(event.status) || !isCachedExport) {
    queryClient.invalidateQueries({ queryKey })
    return
  }
  queryClient.setQueriesData<SettlementExportRecord[]>({ queryKey }, (records) =>
    records ? applySettlementExportEvent(records, event) : records
  )
}

/**
 * Listens for `settlement_export_updated` on the shared system channel, and only while the export
 * dialog is open. The caller owns the channel: a subscription of this feature's own would
 * unsubscribe the shared channel in its cleanup and cut off every other event on it, so closing the
 * dialog unbinds only this handler. The dialog refetches its list on open, so nothing is missed.
 */
export function useSettlementExportUpdates(systemChannel: Channel | null, systemId: number | undefined): void {
  const { isDialogOpen } = useSnapshot(settlementExportStore)

  useEvent(isDialogOpen ? systemChannel : null, SETTLEMENT_EXPORT_UPDATED_EVENT, (data: unknown) => {
    if (systemId !== undefined) handleSettlementExportUpdatedEvent(systemId, data)
  })
}

const CachedListWindowSchema = z.object({ createdAfter: z.string(), createdBefore: z.string() })

/**
 * Adds a just-created export to every cached list of its system whose request window holds its
 * `created_at`, so it shows at once even when the `Created` event is late or lost. The event usually
 * arrives first and refetches the list anyway: it names an export no cached list holds yet. Lists are
 * newest first, and a new export is the newest.
 */
export function insertCreatedSettlementExport(record: SettlementExportRecord): void {
  const createdAt = Date.parse(record.created_at)
  const cachedLists = queryClient.getQueriesData<SettlementExportRecord[]>({
    queryKey: [SETTLEMENT_EXPORTS_QUERY_KEY, { system: record.system }],
  })

  for (const [queryKey, records] of cachedLists) {
    const window = CachedListWindowSchema.safeParse(queryKey[1])
    if (!records || !window.success || records.some(({ id }) => id === record.id)) continue
    const isInWindow =
      Date.parse(window.data.createdAfter) <= createdAt && createdAt < Date.parse(window.data.createdBefore)
    if (isInWindow) queryClient.setQueryData(queryKey, [record, ...records])
  }
}

export function useSettlementExports(window: SettlementExportListWindow | null) {
  const { systemId } = useAuth()

  return useQuery({
    queryKey: [SETTLEMENT_EXPORTS_QUERY_KEY, { system: systemId, ...window }],
    queryFn: async () =>
      z.array(SettlementExportRecordSchema).parse(
        await apiClient(SETTLEMENT_EXPORTS_ENDPOINT, {
          params: {
            system: systemId,
            created_after: window?.createdAfter,
            created_before: window?.createdBefore,
          },
        })
      ),
    enabled: !!systemId && !!window,
    // Status events are only listened to while the dialog is open, so each opening starts fresh.
    refetchOnMount: 'always',
    // A response that breaks the backend contract will not fix itself on a retry.
    retry: (failureCount, error) => !(error instanceof z.ZodError) && shouldRetryQuery(failureCount, error),
  })
}

/** Events sent while the socket was down are not replayed, so a reconnect refetches the list. */
export function useRefetchSettlementExportsOnPusherReconnect(): void {
  const connectionState = usePusherConnectionState()
  const previousConnectionState = useRef(connectionState)

  useEffect(() => {
    const isReconnect = connectionState === 'connected' && previousConnectionState.current !== 'connected'
    previousConnectionState.current = connectionState
    if (isReconnect) {
      queryClient.invalidateQueries({ queryKey: [SETTLEMENT_EXPORTS_QUERY_KEY] })
    }
  }, [connectionState])
}

export function useCreateSettlementExportMutation() {
  const { t } = useTranslation()

  return useMutation({
    /**
     * Resolves to `null` when the backend accepted the export but its response does not match the
     * schema: the export is queued either way, and reporting a failure would invite running it twice.
     */
    mutationFn: async (body: CreateSettlementExportBody): Promise<SettlementExportRecord | null> => {
      const parsed = SettlementExportRecordSchema.safeParse(
        await apiClient(SETTLEMENT_EXPORTS_CREATE_ENDPOINT, { data: body })
      )
      if (parsed.success) return parsed.data
      log.error('Settlement export was created, but its response does not match the schema:', parsed.error)
      return null
    },
    onSuccess: (record, body) => {
      if (record) {
        insertCreatedSettlementExport(record)
      } else {
        queryClient.invalidateQueries({ queryKey: [SETTLEMENT_EXPORTS_QUERY_KEY, { system: body.system }] })
      }
      alertStore.success(t(`The settlement export has been started.`))
    },
    onError: (error) => {
      alertStore.error(getSettlementExportErrorMessage(error) ?? t(`The settlement export could not be started.`))
    },
  })
}

export function useDownloadSettlementExportMutation() {
  const { t } = useTranslation()

  return useMutation({
    mutationFn: async (record: SettlementExportRecord) => {
      const response = await spreadsheetApiClient(settlementExportDownloadEndpoint(record.id))
      if (!response?.result) {
        throw new Error(`Settlement export ${record.id} returned no file`)
      }
      // The API sets no Access-Control-Expose-Headers, so Content-Disposition is unreadable
      // cross-origin; the record carries the file name instead.
      saveAs(
        new Blob([response.result], { type: XLSX_CONTENT_TYPE }),
        record.file_name || `settlement_export_${record.id}.xlsx`
      )
    },
    onError: (error) => {
      const status = getErrorStatus(error)
      if (status === 403) {
        alertStore.error(t(`You do not have access to this settlement export.`))
      } else if (status === 404) {
        alertStore.error(t(`The file for this settlement export is not available.`))
      } else {
        alertStore.error(t(`The settlement export could not be downloaded.`))
      }
    },
  })
}
